<!-- Coffee — one bag, as a page (/tabs/shelf/coffee/:slug).

     The Label sheet's content as a normal screen, for the list view, deep links
     and screen readers: the bag held up at the top (tap it to turn it over to
     the roaster's photos), the label under it, and the same one primary action. -->
<template>
  <IonPage>
    <AppHeader :title="coffee ? coffee.roaster : 'Coffee'" back="/tabs/shelf" />

    <IonContent :fullscreen="true" class="coffee-page">
      <template v-if="coffee">
        <BagHero class="hero" :coffee="coffee" :size="size" tall />

        <div class="content">
          <CoffeeLabel :coffee="coffee">
            <template #buy>
              <SizePicker v-model:size="size" v-model:qty="qty" :offers="coffee.offers" />
            </template>
          </CoffeeLabel>

          <div class="ar">
            <button type="button" class="ar__btn" :disabled="ar.busy.value" @click="ar.run()">
              <IonSpinner v-if="ar.busy.value" name="crescent" class="ar__spin" aria-hidden="true" />
              <IonIcon v-else :icon="cubeOutline" aria-hidden="true" />
              {{ ar.busy.value ? 'Building your shelf…' : 'See it in your room' }}
            </button>
            <p v-if="ar.error.value" class="field-error" role="alert">{{ ar.error.value }}</p>
          </div>
        </div>

        <ActionRail>
          <IonButton
            expand="block"
            class="add"
            :class="{ 'add--done': added }"
            :disabled="soldOut || add.busy.value"
            @click="add.run()"
          >
            {{ label }}
          </IonButton>
          <p v-if="add.error.value" class="field-error" role="alert">{{ add.error.value }}</p>
          <p v-else-if="soldOut" class="rail-note">Every size is sold out on BeanHub right now.</p>
        </ActionRail>
      </template>

      <!-- A deep link can outlive the coffee: the range changes every week. -->
      <div v-else class="empty">
        <h2 class="section-title">We couldn't find that coffee.</h2>
        <p>It may have sold through or left the range since this link was made.</p>
        <IonButton class="empty__btn" @click="browse">Walk the shelves</IonButton>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { IonPage, IonContent, IonButton, IonIcon, IonSpinner } from '@ionic/vue';
import { cubeOutline } from 'ionicons/icons';
import AppHeader from '../core/ui/AppHeader.vue';
import ActionRail from '../core/ui/ActionRail.vue';
import { useAction } from '../core/ui/index.js';
import { coffeeBySlug } from '../store.js';
import CoffeeLabel from '../components/CoffeeLabel.vue';
import SizePicker, { useAddToBag } from '../components/SizePicker.vue';
import BagHero from '../components/BagHero.vue';
import { seeInRoom } from '../lib/see-in-room.js';

const props = defineProps({
  slug: { type: String, required: true },
});

const router = useRouter();
const coffee = computed(() => coffeeBySlug(props.slug));

const { size, qty, soldOut, added, add, label } = useAddToBag(coffee);

// --- AR -----------------------------------------------------------------------
// This coffee in your room (src/lib/see-in-room.js): the walkable shop with it
// in your hand where the device can, else the coffee on its own board in AR
// Quick Look with an "Add to bag" banner for the size chosen here.
const ar = useAction(async () => {
  const c = coffee.value;
  if (c) await seeInRoom({ pick: c.slug, size: size.value });
}, { failed: 'Couldn\'t build your shelf. Try again.' });

const browse = () => router.replace('/tabs/shelf');
</script>

<style scoped>
/* No bottom padding on the content, on purpose. `<ActionRail>` is sticky
   and IN FLOW at the end of the content, so at the bottom of the scroll it
   sits after the last row rather than over it: the last row always scrolls
   fully clear. Padding the scroll box does the opposite of what it looks like
   — a `bottom: 0` sticky element pins to the padding edge, so the rail is
   lifted off the bottom by the padding and floats mid-screen with rows
   passing under it (see core/ui/ActionRail.vue). The tab bar needs no
   clearance here either: IonTabs lays the outlet out ABOVE the bar, so the
   content already ends where the bar begins, and `--tabbar-clearance` is for
   what is presented from the app root (ion-toast), not page content. */
.coffee-page {
  --background: var(--paper, #F4EFE6);
}

.hero { padding-top: var(--sp-3); }

.content {
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
  max-width: 40rem;
  margin: 0 auto;
  padding: var(--sp-6) var(--gutter) var(--sp-5);
}

.ar { display: grid; gap: var(--sp-2); justify-items: start; }
.ar__btn {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  min-height: var(--tap);
  padding: 0 var(--sp-4) 0 var(--sp-3);
  border: 1px solid var(--line, #ECE6DC);
  border-radius: 999px;
  background: var(--dock, #FAF7F1);
  color: var(--ink, #1F2A22);
  font: inherit;
  font-size: var(--text-sm);
  font-weight: 700;
  cursor: pointer;
  transition: background var(--dur) var(--ease);
}
.ar__btn ion-icon { font-size: var(--text-lg); }
.ar__btn:hover:not(:disabled) { background: var(--sage, #E6EDE4); }
.ar__btn:disabled { color: var(--muted, #5E6B60); cursor: progress; }
.ar__btn:focus-visible { outline: 2px solid var(--ink, #1F2A22); outline-offset: 2px; }
.ar__spin { width: var(--text-lg); height: var(--text-lg); }
.ar .field-error { margin-top: 0; }

/* The one primary: ink pill, 52 tall, dock text. */
.add,
.empty__btn {
  --background: var(--ink, #1F2A22);
  --background-hover: var(--ink, #1F2A22);
  --background-activated: var(--ink, #1F2A22);
  --background-focused: var(--ink, #1F2A22);
  --color: var(--dock, #FAF7F1);
  --border-radius: 999px;
  --box-shadow: none;
  height: calc(var(--sp-7) + var(--sp-1)); /* 52 */
  margin: 0;
  font-family: var(--font-body, 'Manrope', system-ui, sans-serif);
  font-size: calc(var(--text-md) * 1.0625); /* 17 */
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
  font-variant-numeric: tabular-nums;
  transition: transform 120ms var(--ease);
}
.add:active, .empty__btn:active { transform: scale(0.97); }
@media (prefers-reduced-motion: reduce) { .add, .add:active { transform: none; transition: none; } }
.add--done { --background: var(--leaf, #3F7A4E); --background-activated: var(--leaf, #3F7A4E); --background-hover: var(--leaf, #3F7A4E); }
.add[disabled] { opacity: 0.4; }
.rail-note { margin: 0; color: var(--muted, #5E6B60); font-size: var(--text-sm); text-align: center; }
.coffee-page :deep(.action-rail) { background: var(--paper, #F4EFE6); }

.empty { padding: var(--sp-8) var(--gutter); text-align: center; }
.empty .section-title { margin-top: 0; font-family: var(--font-display, 'Bricolage Grotesque', 'Manrope', system-ui, sans-serif); letter-spacing: -0.02em; }
.empty p { margin: 0 auto var(--sp-5); max-width: 22rem; color: var(--muted, #5E6B60); line-height: 1.5; }
.empty__btn { padding: 0 var(--sp-5); }
</style>
